/**
 * The panel assistant's face: its animated robot avatar with a badge
 * in the bottom-right corner. The Client Assistant wears the signed-in user's
 * photo; the Practice Assistant wears the firm's logo, or no badge without one.
 */
import type { FC } from "react";
import { useSelector } from "react-redux";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { initials } from "@/features/caseIde/chat/mentions";
import { useFirmRecord } from "@/features/lawFirms/firmQuery";
import { normalizeAvatarSrc } from "@/helpers/userDisplay";
import { useGetUserDetails } from "@/hooks/useGetUserDetails";
import { cn } from "@/lib/utils";
import { lawyerSelectors } from "@/redux/lawyer/selectors";

import type { PanelAgent } from "../agentPanelStore";
import { AgentMoodAvatar } from "./AgentMoodAvatar";

type Badge =
  | { kind: "user"; src: string; name: string }
  | { kind: "firm"; src: string }
  | null;

/** The firm's logo URL, if the firm has one. */
const useFirmLogo = (enabled: boolean): string => {
  const { law_firm_id, lawFirmLogo } = useSelector(lawyerSelectors.selectAll);
  const lawFirmId = (law_firm_id ?? "").trim();
  const { data } = useFirmRecord(lawFirmId, enabled);
  return (data?.logo_url ?? "").trim() || (lawFirmLogo ?? "").trim();
};

export const useAssistantBadge = (agent: PanelAgent): Badge => {
  const {
    userDetails: { userFullName, avatarSrc }
  } = useGetUserDetails();
  const firmLogo = useFirmLogo(agent === "practice");
  if (agent === "client")
    return {
      kind: "user",
      src: normalizeAvatarSrc(avatarSrc),
      name: userFullName
    };
  return firmLogo ? { kind: "firm", src: firmLogo } : null;
};

const BADGE =
  "absolute -right-1 -bottom-1 overflow-hidden rounded-xs bg-background ring-cutout ring-background";

/** `sm` for headers (28px), `lg` for the dashboard hero (36px). */
const SIZES = {
  sm: { face: "size-7", badge: "size-3.5", text: "text-xs" },
  lg: { face: "size-9", badge: "size-4", text: "text-xs" }
} as const;

type Props = {
  agent: PanelAgent;
  /** The avatar seed, `"<scope_kind>:<scope_id>"`. */
  seed: string;
  size?: keyof typeof SIZES;
  className?: string;
};

export const AssistantAvatar: FC<Props> = ({
  agent,
  seed,
  size = "sm",
  className
}) => {
  const badge = useAssistantBadge(agent);
  const s = SIZES[size];
  return (
    <span
      className={cn("relative inline-flex shrink-0", className)}
      aria-hidden
      data-testid="assistant-avatar"
      data-agent={agent}
      data-seed={seed}
    >
      <AgentMoodAvatar
        animated
        size="md"
        className={s.face}
        agent={{ key: seed }}
      />
      {badge?.kind === "user" ? (
        <Avatar
          shape="square"
          className={cn(BADGE, s.badge, "after:hidden")}
          data-testid="assistant-avatar-badge"
          data-badge="user"
        >
          {badge.src ? <AvatarImage src={badge.src} alt="" /> : null}
          <AvatarFallback className={cn(s.text, "font-medium")}>
            {initials(badge.name).slice(0, 1)}
          </AvatarFallback>
        </Avatar>
      ) : badge?.kind === "firm" ? (
        <img
          src={badge.src}
          alt=""
          className={cn(BADGE, s.badge, "object-contain")}
          data-testid="assistant-avatar-badge"
          data-badge="firm"
        />
      ) : null}
    </span>
  );
};
